import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { postDeleteSubcategory, postDeleteCategory, checkCategoryHasSubcategory } from '../../../api/rentals';
import { getCategoryData } from '../../../redux/slices/rentals';
import { useDispatch, useSelector } from '../../../redux/store';
import { AwaitButton } from '../../../reusable-components/await-button';
import CloseIconButton from '../../../reusable-components/basic-buttons/CloseIconButton';
import { useSnackbar } from '../../../reusable-components/snackbar';

DeleteRentalCategory.propTypes = {
  row: PropTypes.object
};

export default function DeleteRentalCategory({ row }) {
  const {
    data: { categoryData },
  } = useSelector((state) => state.rentals);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [category, setCategory] = useState(null);
  const [subcategory, setSubcategory] = useState(null);

  const seenCategories = new Set();

  const categoryOptions = useMemo(
    () =>
      categoryData.reduce((acc, item) => {
        if (!seenCategories.has(item.categoryName)) {
          seenCategories.add(item.categoryName);
          acc.push({
            label: item.categoryName,
            id: item.categoryId,
          });
        }
        return acc;
      }, []),
    [categoryData]
  );

  const subcategoryOptions = useMemo(
    () =>
      categoryData
        .filter((s) => s.subcategoryId != null)
        .map((item) => ({
          label: item.subcategoryName,
          id: item.subcategoryId,
          categoryId: item.categoryId,
        })),
    [categoryData]
  );

  const handleClose = () => {
    setOpen(false);
    setCategory(null);
  };

  const handleDelete = async () => {
    if (row.mainCategoryId) {
      const response = await postDeleteSubcategory({
        id: row.subId,
        substituteSubcategoryId: subcategory.id,
        substituteCategoryId: category.id,
      });
      if (!response.data.success) {
        enqueueSnackbar('Subcategory cannot be deleted', { variant: 'error' });
        return;
      }
      enqueueSnackbar(`${row.name} removed`, { variant: 'success' });
      dispatch(getCategoryData());
    } else {
      const response = await postDeleteCategory({id: row.id});
      if (!response.data.success) {
        enqueueSnackbar('Category cannot be deleted', { variant: 'error' });
        return;
      }
      enqueueSnackbar(`${row.name} removed`, { variant: 'success' });
      dispatch(getCategoryData());
    }
    setOpen(false);
  };

   const checkHasSubcategory = async () => {
    try{
      if (!row.mainCategoryId) {
        const response = await checkCategoryHasSubcategory(row.id);
        if (response.data) {
          enqueueSnackbar('You cannot delete a main category while it has subcategories', { variant: 'error' });
          return;
        }
        setOpen(true);
      } else {
        setOpen(true);
      }
      } catch (error) {
        enqueueSnackbar('Error checking category', { variant: 'error' });
    }

      
    };
  return (
    <>
      <Tooltip title="Delete Subcategory">
        <IconButton onClick={checkHasSubcategory}>
          <DeleteOutlineIcon color="error" />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
          }}
        >
           Delete {row?.mainCategoryId ? 'Subcategory' : 'Category'}
         <CloseIconButton onClick={handleClose}/>
        </DialogTitle>
        <DialogContent sx={{ height: '100%', width: '100%' }}>
          <>
          {row.mainCategoryId ? (
            <>
            <Typography>Switch existing items from {row.name} *</Typography>
            <DialogContentText sx={{ mt: 2, fontWeight: 'bold' }}>Main Category</DialogContentText>
            <Autocomplete
              sx={{
                mt: 1,
                '& legend': { display: 'none' },
                '& fieldset': { top: 0 },
                '& .MuiFormLabel-root ': 'none',
              }}
              size="small"
              fullWidth
              type="new-password"
              options={categoryOptions}
              autoHighlight
              getOptionLabel={(option) => option?.label}
              value={category}
              onChange={(event, newValue) => {
                setCategory(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label=""
                  variant="outlined"
                  inputProps={{
                  ...params.inputProps,
                  }}
                  autoComplete="off"
                />
              )}
              />
              <DialogContentText sx={{ mt: 2, fontWeight: 'bold' }}>Subcategory</DialogContentText>
              <Autocomplete
                sx={{
                  width: '100%',
                  mt: 1,
                  '& legend': { display: 'none' },
                  '& fieldset': { top: 0 },
                  '& .MuiFormLabel-root ': 'none',
                }}
                size="small"
                type="new-password"
                options={subcategoryOptions
                .filter((s) => s.categoryId === category?.id)
                }
                getOptionDisabled={(option) => option.id === row.subId}
                autoHighlight
                getOptionLabel={(option) => option?.label}
                value={subcategory}
                onChange={(event, newValue) => {
                setSubcategory(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label=""
                  variant="outlined"
                  inputProps={{
                    ...params.inputProps,
                  }}
                  autoComplete="off"
                />
              )}
            />
          </>) : (
            <>
              <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>
                Are you sure you want to delete {row.name}
              </DialogContentText>
            </>
          )}
        </>
        </DialogContent>
        <DialogActions>
          <AwaitButton
            variant="outlined"
            color="error"
            size="medium"
            sx={{ width: 150, mr: 2 }}
            onClick={handleDelete}
            disabled={row.subcategoryId && (!category || !subcategory)}
          >
            Delete
          </AwaitButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
